import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import Navigation from "../../components/Navigation/Navigation";
import {
  createStackerType,
  createStackerTypeVersion,
  fetchStackerProjectSetup,
  fetchStackerProjectsList,
  fetchStackerTypes,
  saveStackerProjectSetup,
  setStackerProjectVisibility,
} from "../../api/stackersApi";
import { UserAuth } from "../../components/Context/AuthContext";

const INPUT_KIND_OPTIONS = [
  "checkbox_group",
  "text",
  "number",
  "select",
  "photo_group",
];

const makeEmptyField = () => ({
  fieldKey: "",
  label: "",
  inputKind: "text",
  required: false,
  showInPdf: true,
});

const makeEmptyStep = () => ({
  stepKey: "",
  stepName: "",
  quantityPerUnit: 0,
  includeInCompletion: true,
  weightPercent: "",
});

const makeEmptyTypeMix = () => ({
  typeId: "",
  totalUnits: 0,
  posts: "",
});

/**
 * Build the unit list from the type mix WITHOUT renumbering units that already
 * exist in the saved setup. Existing units keep their number and type; when a
 * type's count grows, new units are appended after the highest existing unit
 * number; when it shrinks, the highest-numbered units of that type are dropped.
 *
 * Numbering purely by mix order (the old behaviour) re-typed existing units
 * whenever an earlier type in the mix grew, stranding their recorded progress.
 */
function buildUnitsFromMix(typeMix, existingUnits = []) {
  const units = [];
  const existingByType = new Map();
  let maxExisting = 0;
  existingUnits.forEach((u) => {
    const typeId = Number(u.typeId || 0);
    const unitNumber = Number(u.unitNumber || 0);
    if (!typeId || !unitNumber) return;
    if (!existingByType.has(typeId)) existingByType.set(typeId, []);
    existingByType.get(typeId).push(unitNumber);
    if (unitNumber > maxExisting) maxExisting = unitNumber;
  });
  let nextUnit = maxExisting + 1;
  typeMix.forEach((row) => {
    const count = Number(row.totalUnits || 0);
    const typeId = Number(row.typeId || 0);
    if (!count || !typeId) return;
    const kept = (existingByType.get(typeId) || []).slice().sort((a, b) => a - b).slice(0, count);
    kept.forEach((unitNumber) => units.push({ unitNumber, typeId }));
    for (let i = kept.length; i < count; i += 1) {
      units.push({ unitNumber: nextUnit, typeId });
      nextUnit += 1;
    }
  });
  return units.sort((a, b) => a.unitNumber - b.unitNumber);
}

export default function AdminStackersSetupPage() {
  const { userStatusValue } = UserAuth();
  const token = sessionStorage.getItem("token");

  const [activeTab, setActiveTab] = useState("project");
  const [isBootLoading, setIsBootLoading] = useState(true);
  const [types, setTypes] = useState([]);
  const [globalError, setGlobalError] = useState("");
  const [globalSuccess, setGlobalSuccess] = useState("");

  // Type onboarding
  const [typeMode, setTypeMode] = useState("new");
  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [typeCode, setTypeCode] = useState("");
  const [typeDisplayName, setTypeDisplayName] = useState("");
  const [typeFields, setTypeFields] = useState([makeEmptyField()]);
  const [typeSteps, setTypeSteps] = useState([makeEmptyStep()]);
  const [isSavingType, setIsSavingType] = useState(false);

  // Project setup
  const [projectName, setProjectName] = useState("");
  const [existingProjects, setExistingProjects] = useState([]);
  const [selectedExistingProject, setSelectedExistingProject] = useState("");
  const [locationName, setLocationName] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerContact, setCustomerContact] = useState("");
  const [reportRecipientsRaw, setReportRecipientsRaw] = useState("");
  const [kickoffDate, setKickoffDate] = useState("");
  const [defaultPosts, setDefaultPosts] = useState("");
  const [enableV2, setEnableV2] = useState(false);
  const [typeMix, setTypeMix] = useState([makeEmptyTypeMix()]);
  const [isSavingProject, setIsSavingProject] = useState(false);
  const [isLoadingProject, setIsLoadingProject] = useState(false);
  const [previewUnits, setPreviewUnits] = useState([]);
  // Units of the currently loaded project's saved setup; keeps numbering stable on save.
  const [existingSetupUnits, setExistingSetupUnits] = useState({ project: "", units: [] });

  // Stackers tab visibility
  const [visibilityUpdatingId, setVisibilityUpdatingId] = useState(null);

  const typeOptions = useMemo(
    () =>
      (types || []).map((type) => ({
        value: String(type.id),
        label: `${type.displayName} (${type.code})`,
        raw: type,
      })),
    [types]
  );

  const refreshTypes = async () => {
    const fetched = await fetchStackerTypes(token);
    setTypes(fetched);
    return fetched;
  };

  const refreshProjectsList = async () => {
    try {
      const list = await fetchStackerProjectsList(token);
      setExistingProjects(list);
      return list;
    } catch (error) {
      setExistingProjects([]);
      return [];
    }
  };

  useEffect(() => {
    let isMounted = true;
    const init = async () => {
      setIsBootLoading(true);
      setGlobalError("");
      try {
        const [fetched, projectsList] = await Promise.all([
          fetchStackerTypes(token),
          fetchStackerProjectsList(token).catch(() => []),
        ]);
        if (isMounted) {
          setTypes(fetched);
          setExistingProjects(projectsList);
        }
      } catch (error) {
        if (isMounted) setGlobalError(error.message || "Failed to load stacker admin data.");
      } finally {
        if (isMounted) setIsBootLoading(false);
      }
    };
    init();
    return () => {
      isMounted = false;
    };
  }, [token]);

  const handleToggleVisibility = async (proj) => {
    if (!proj || visibilityUpdatingId) return;
    const nextHidden = !proj.isHidden;
    try {
      setVisibilityUpdatingId(proj.projectId);
      setGlobalError("");
      setGlobalSuccess("");
      await setStackerProjectVisibility(proj.projectId, nextHidden, token);
      setExistingProjects((prev) =>
        prev.map((p) => (p.projectId === proj.projectId ? { ...p, isHidden: nextHidden } : p))
      );
      // Drop the summary page's cached project list so the change shows immediately
      localStorage.removeItem("stackerProjects_v2");
      localStorage.removeItem("stackerProjectsTimestamp_v2");
      setGlobalSuccess(
        `${proj.projectName} is now ${nextHidden ? "hidden from" : "visible on"} the Stackers tab.`
      );
    } catch (error) {
      setGlobalError(error.message || "Failed to update project visibility.");
    } finally {
      setVisibilityUpdatingId(null);
    }
  };

  const existingUnitsForProject =
    existingSetupUnits.project && existingSetupUnits.project === projectName.trim()
      ? existingSetupUnits.units
      : [];

  useEffect(() => {
    setPreviewUnits(buildUnitsFromMix(typeMix, existingUnitsForProject));
  }, [typeMix, existingUnitsForProject]);

  const onSelectTypeForVersion = (nextTypeId) => {
    setSelectedTypeId(nextTypeId);
    const selected = (types || []).find((t) => String(t.id) === String(nextTypeId));
    if (!selected) return;
    setTypeCode(selected.code || "");
    setTypeDisplayName(selected.displayName || "");
    setTypeFields(
      (selected.fields || []).length
        ? selected.fields.map((field) => ({
            fieldKey: field.fieldKey || "",
            label: field.label || "",
            inputKind: field.inputKind || "text",
            required: !!field.required,
            showInPdf: field.showInPdf !== false,
          }))
        : [makeEmptyField()]
    );
    setTypeSteps(
      (selected.steps || []).length
        ? selected.steps.map((step) => ({
            stepKey: step.stepKey || "",
            stepName: step.stepName || "",
            quantityPerUnit: Number(step.quantityPerUnit || 0),
            includeInCompletion: step.includeInCompletion !== false,
            weightPercent: step.weightPercent != null ? step.weightPercent : "",
          }))
        : [makeEmptyStep()]
    );
  };

  const updateFieldRow = (index, key, value) => {
    setTypeFields((prev) =>
      prev.map((row, idx) => (idx === index ? { ...row, [key]: value } : row))
    );
  };

  const updateStepRow = (index, key, value) => {
    setTypeSteps((prev) =>
      prev.map((row, idx) => (idx === index ? { ...row, [key]: value } : row))
    );
  };

  const updateTypeMixRow = (index, key, value) => {
    setTypeMix((prev) =>
      prev.map((row, idx) => (idx === index ? { ...row, [key]: value } : row))
    );
  };

  const validateTypePayload = () => {
    if (!typeDisplayName.trim()) return "Type display name is required.";
    if (typeMode === "new" && !typeCode.trim()) return "Type code is required for new type.";
    if (!typeFields.some((f) => String(f.fieldKey || "").trim())) {
      return "Add at least one field.";
    }
    if (!typeSteps.some((s) => String(s.stepKey || "").trim())) {
      return "Add at least one step.";
    }
    return "";
  };

  const saveTypeSchema = async () => {
    setGlobalError("");
    setGlobalSuccess("");
    const validationError = validateTypePayload();
    if (validationError) {
      setGlobalError(validationError);
      return;
    }

    const payload = {
      code: typeCode,
      displayName: typeDisplayName,
      fields: typeFields
        .filter((f) => String(f.fieldKey || "").trim())
        .map((f) => ({
          fieldKey: String(f.fieldKey).trim(),
          label: String(f.label || f.fieldKey).trim(),
          inputKind: f.inputKind,
          required: !!f.required,
          showInPdf: !!f.showInPdf,
        })),
      steps: typeSteps
        .filter((s) => String(s.stepKey || "").trim())
        .map((s) => ({
          stepKey: String(s.stepKey).trim(),
          stepName: String(s.stepName || s.stepKey).trim(),
          quantityPerUnit: Number(s.quantityPerUnit || 0),
          includeInCompletion: !!s.includeInCompletion,
          weightPercent:
            s.weightPercent === "" || s.weightPercent == null
              ? null
              : Number(s.weightPercent),
        })),
      publish: true,
    };

    setIsSavingType(true);
    try {
      if (typeMode === "new") {
        await createStackerType(payload, token);
      } else {
        if (!selectedTypeId) throw new Error("Choose a stacker type to version.");
        await createStackerTypeVersion(selectedTypeId, payload, token);
      }
      await refreshTypes();
      setGlobalSuccess(
        typeMode === "new"
          ? "Stacker type created successfully."
          : "New schema version created successfully."
      );
      if (typeMode === "new") {
        setTypeCode("");
        setTypeDisplayName("");
        setTypeFields([makeEmptyField()]);
        setTypeSteps([makeEmptyStep()]);
      }
    } catch (error) {
      setGlobalError(error.message || "Failed to save stacker type.");
    } finally {
      setIsSavingType(false);
    }
  };

  const loadProjectSetup = async (overrideName) => {
    setGlobalError("");
    setGlobalSuccess("");
    const target = (overrideName != null ? overrideName : projectName).trim();
    if (!target) {
      setGlobalError("Choose or enter a project name to load setup.");
      return;
    }
    setIsLoadingProject(true);
    try {
      const data = await fetchStackerProjectSetup(target, token);
      setProjectName(target);
      setLocationName(data.locationName || "");
      setCustomerName(data.customerName || "");
      setCustomerContact(data.customerContact || "");
      setReportRecipientsRaw((data.reportRecipients || []).join(", "));
      setKickoffDate(data.kickoffDate ? String(data.kickoffDate).slice(0, 10) : "");
      setDefaultPosts(data.defaultPosts != null ? String(data.defaultPosts) : "");
      setEnableV2(!!data.v2Enabled);
      setExistingSetupUnits({
        project: target,
        units: (data.units || []).map((u) => ({ unitNumber: Number(u.unitNumber), typeId: Number(u.typeId) })),
      });
      setTypeMix(
        (data.types || []).length
          ? data.types.map((t) => ({
              typeId: String(t.typeId),
              totalUnits: Number(t.totalUnits || 0),
              posts: "",
            }))
          : [makeEmptyTypeMix()]
      );
      setGlobalSuccess("Project setup loaded.");
    } catch (error) {
      setGlobalError(error.message || "Failed to load project setup.");
    } finally {
      setIsLoadingProject(false);
    }
  };

  const saveProjectSetup = async () => {
    setGlobalError("");
    setGlobalSuccess("");
    if (!projectName.trim()) {
      setGlobalError("Project name is required.");
      return;
    }
    const selectedMix = typeMix.filter((row) => Number(row.typeId || 0) && Number(row.totalUnits || 0) > 0);
    if (!selectedMix.length) {
      setGlobalError("Add at least one stacker type with units.");
      return;
    }

    const recipients = reportRecipientsRaw
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);

    const payload = {
      projectName: projectName.trim(),
      locationName: locationName.trim(),
      customerName: customerName.trim(),
      customerContact: customerContact.trim(),
      reportRecipients: recipients,
      kickoffDate: kickoffDate || null,
      defaultPosts: defaultPosts === "" ? null : Number(defaultPosts),
      enableV2,
      types: selectedMix.map((row) => {
        const typeMeta = (types || []).find((t) => String(t.id) === String(row.typeId));
        return {
          typeId: Number(row.typeId),
          code: typeMeta?.code || undefined,
          totalUnits: Number(row.totalUnits),
          posts: row.posts === "" ? null : Number(row.posts),
        };
      }),
      units: buildUnitsFromMix(selectedMix, existingUnitsForProject),
    };

    setIsSavingProject(true);
    try {
      await saveStackerProjectSetup(payload, token);
      setExistingSetupUnits({ project: payload.projectName, units: payload.units });
      try {
        localStorage.removeItem("stackerProjects");
        localStorage.removeItem("stackerProjectsTimestamp");
      } catch (_) {}
      await refreshProjectsList();
      setGlobalSuccess("Stacker project setup saved.");
    } catch (error) {
      setGlobalError(error.message || "Failed to save project setup.");
    } finally {
      setIsSavingProject(false);
    }
  };

  if (userStatusValue < 1) {
    return (
      <>
        <Navigation />
        <div className="container mt-4">
          <Alert variant="warning">Admin access required.</Alert>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="container mt-4 pb-5">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h3 className="m-0">Stackers Admin Setup</h3>
          <div className="d-flex gap-2">
            <Button
              variant={activeTab === "project" ? "primary" : "outline-primary"}
              onClick={() => setActiveTab("project")}
            >
              Project Setup
            </Button>
            <Button
              variant={activeTab === "types" ? "primary" : "outline-primary"}
              onClick={() => setActiveTab("types")}
            >
              Type Onboarding
            </Button>
            <Button
              variant={activeTab === "visibility" ? "primary" : "outline-primary"}
              onClick={() => setActiveTab("visibility")}
            >
              Tab Visibility
            </Button>
          </div>
        </div>

        {globalError ? <Alert variant="danger">{globalError}</Alert> : null}
        {globalSuccess ? <Alert variant="success">{globalSuccess}</Alert> : null}

        {isBootLoading ? (
          <div className="d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" />
            <span>Loading stacker admin data...</span>
          </div>
        ) : null}

        {!isBootLoading && activeTab === "visibility" ? (
          <Card className="mb-4">
            <Card.Body>
              <Card.Title className="mb-1">Stackers tab visibility</Card.Title>
              <p className="text-muted small mb-3">
                Hidden projects stop appearing on the Stackers tab of the summary page. They stay
                available everywhere else (reports, project setup) and can be shown again at any time.
              </p>
              {existingProjects.length === 0 ? (
                <p className="text-muted mb-0">No projects found.</p>
              ) : (
                <Table size="sm" responsive className="align-middle">
                  <thead>
                    <tr>
                      <th>Project</th>
                      <th>Status</th>
                      <th className="text-end">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {existingProjects.map((proj) => (
                      <tr key={proj.projectId}>
                        <td>{proj.projectName}</td>
                        <td>
                          {proj.isHidden ? (
                            <span className="badge bg-secondary">Hidden</span>
                          ) : (
                            <span className="badge bg-success">Visible</span>
                          )}
                        </td>
                        <td className="text-end">
                          <Button
                            size="sm"
                            variant={proj.isHidden ? "outline-success" : "outline-secondary"}
                            disabled={visibilityUpdatingId === proj.projectId}
                            onClick={() => handleToggleVisibility(proj)}
                          >
                            {visibilityUpdatingId === proj.projectId
                              ? "Saving..."
                              : proj.isHidden
                                ? "Show on tab"
                                : "Hide from tab"}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        ) : null}

        {!isBootLoading && activeTab === "types" ? (
          <Card className="mb-3">
            <Card.Body>
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Label>Mode</Form.Label>
                  <Form.Select value={typeMode} onChange={(e) => setTypeMode(e.target.value)}>
                    <option value="new">Create New Type</option>
                    <option value="version">Create New Version</option>
                  </Form.Select>
                </Col>
                {typeMode === "version" ? (
                  <Col md={8}>
                    <Form.Label>Existing Type</Form.Label>
                    <Form.Select
                      value={selectedTypeId}
                      onChange={(e) => onSelectTypeForVersion(e.target.value)}
                    >
                      <option value="">Select type...</option>
                      {typeOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                ) : null}
              </Row>

              <Row className="mb-3">
                <Col md={4}>
                  <Form.Label>Type Code</Form.Label>
                  <Form.Control
                    value={typeCode}
                    onChange={(e) => setTypeCode(e.target.value)}
                    disabled={typeMode === "version"}
                    placeholder="aps_fs2000"
                  />
                </Col>
                <Col md={8}>
                  <Form.Label>Display Name</Form.Label>
                  <Form.Control
                    value={typeDisplayName}
                    onChange={(e) => setTypeDisplayName(e.target.value)}
                    placeholder="APS FS2000"
                  />
                </Col>
              </Row>

              <h5>Fields</h5>
              <Table bordered size="sm">
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Label</th>
                    <th>Input Kind</th>
                    <th>Required</th>
                    <th>Show in PDF</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {typeFields.map((row, index) => (
                    <tr key={`field-${index}`}>
                      <td>
                        <Form.Control
                          value={row.fieldKey}
                          onChange={(e) => updateFieldRow(index, "fieldKey", e.target.value)}
                          placeholder="crossbeams"
                        />
                      </td>
                      <td>
                        <Form.Control
                          value={row.label}
                          onChange={(e) => updateFieldRow(index, "label", e.target.value)}
                          placeholder="Crossbeams"
                        />
                      </td>
                      <td>
                        <Form.Select
                          value={row.inputKind}
                          onChange={(e) => updateFieldRow(index, "inputKind", e.target.value)}
                        >
                          {INPUT_KIND_OPTIONS.map((kind) => (
                            <option key={kind} value={kind}>
                              {kind}
                            </option>
                          ))}
                        </Form.Select>
                      </td>
                      <td className="text-center">
                        <Form.Check
                          checked={!!row.required}
                          onChange={(e) => updateFieldRow(index, "required", e.target.checked)}
                        />
                      </td>
                      <td className="text-center">
                        <Form.Check
                          checked={row.showInPdf !== false}
                          onChange={(e) => updateFieldRow(index, "showInPdf", e.target.checked)}
                        />
                      </td>
                      <td>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() =>
                            setTypeFields((prev) => prev.filter((_, idx) => idx !== index))
                          }
                          disabled={typeFields.length === 1}
                        >
                          Remove
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <Button variant="outline-secondary" onClick={() => setTypeFields((prev) => [...prev, makeEmptyField()])}>
                Add Field
              </Button>

              <h5 className="mt-4">Completion Steps</h5>
              <Table bordered size="sm">
                <thead>
                  <tr>
                    <th>Step Key</th>
                    <th>Step Name</th>
                    <th>Qty / Unit</th>
                    <th>Weight (% of Total Job)</th>
                    <th>Include in Completion</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {typeSteps.map((row, index) => (
                    <tr key={`step-${index}`}>
                      <td>
                        <Form.Control
                          value={row.stepKey}
                          onChange={(e) => updateStepRow(index, "stepKey", e.target.value)}
                          placeholder="posts"
                        />
                      </td>
                      <td>
                        <Form.Control
                          value={row.stepName}
                          onChange={(e) => updateStepRow(index, "stepName", e.target.value)}
                          placeholder="Posts"
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          value={row.quantityPerUnit}
                          onChange={(e) => updateStepRow(index, "quantityPerUnit", e.target.value)}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          min="0"
                          step="0.01"
                          value={row.weightPercent == null ? "" : row.weightPercent}
                          onChange={(e) => updateStepRow(index, "weightPercent", e.target.value)}
                          placeholder="e.g. 5"
                        />
                      </td>
                      <td className="text-center">
                        <Form.Check
                          checked={row.includeInCompletion !== false}
                          onChange={(e) =>
                            updateStepRow(index, "includeInCompletion", e.target.checked)
                          }
                        />
                      </td>
                      <td>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() =>
                            setTypeSteps((prev) => prev.filter((_, idx) => idx !== index))
                          }
                          disabled={typeSteps.length === 1}
                        >
                          Remove
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <Button variant="outline-secondary" onClick={() => setTypeSteps((prev) => [...prev, makeEmptyStep()])}>
                Add Step
              </Button>

              <div className="mt-4">
                <Button onClick={saveTypeSchema} disabled={isSavingType}>
                  {isSavingType ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Saving...
                    </>
                  ) : typeMode === "new" ? (
                    "Create Type"
                  ) : (
                    "Create Version"
                  )}
                </Button>
              </div>
            </Card.Body>
          </Card>
        ) : null}

        {!isBootLoading && activeTab === "project" ? (
          <Card>
            <Card.Body>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Label>Load Existing Project</Form.Label>
                  <Form.Select
                    value={selectedExistingProject}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedExistingProject(value);
                      if (value) {
                        setProjectName(value);
                        loadProjectSetup(value);
                      }
                    }}
                    disabled={isLoadingProject || existingProjects.length === 0}
                  >
                    <option value="">
                      {existingProjects.length === 0
                        ? "No existing projects yet"
                        : "Select a project to load..."}
                    </option>
                    {existingProjects.map((proj) => (
                      <option key={proj.projectId} value={proj.projectName}>
                        {proj.projectName}
                        {proj.hasSetup ? "" : " (no setup yet)"}
                        {proj.v2Enabled ? " - V2" : ""}
                      </option>
                    ))}
                  </Form.Select>
                  {isLoadingProject && (
                    <small className="text-muted d-block mt-1">
                      <Spinner animation="border" size="sm" className="me-2" />
                      Loading project setup...
                    </small>
                  )}
                </Col>
                <Col md={6}>
                  <Form.Label>Project Name (new or being edited)</Form.Label>
                  <Form.Control
                    value={projectName}
                    onChange={(e) => {
                      setProjectName(e.target.value);
                      setSelectedExistingProject("");
                    }}
                    placeholder="e.g. 502 Waverly - Phase 2"
                  />
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Label>Location</Form.Label>
                  <Form.Control value={locationName} onChange={(e) => setLocationName(e.target.value)} />
                </Col>
                <Col md={6}>
                  <Form.Label>Kickoff Date</Form.Label>
                  <Form.Control type="date" value={kickoffDate} onChange={(e) => setKickoffDate(e.target.value)} />
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Label>Customer Name</Form.Label>
                  <Form.Control value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
                </Col>
                <Col md={6}>
                  <Form.Label>Customer Contact</Form.Label>
                  <Form.Control value={customerContact} onChange={(e) => setCustomerContact(e.target.value)} />
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={8}>
                  <Form.Label>Report Recipients (comma separated)</Form.Label>
                  <Form.Control
                    value={reportRecipientsRaw}
                    onChange={(e) => setReportRecipientsRaw(e.target.value)}
                    placeholder="supervisor@client.com, manager@client.com"
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>Default Posts</Form.Label>
                  <Form.Control
                    type="number"
                    value={defaultPosts}
                    onChange={(e) => setDefaultPosts(e.target.value)}
                  />
                </Col>
              </Row>

              <Form.Check
                className="mb-3"
                label="Enable V2 mixed-type workflow for this project"
                checked={enableV2}
                onChange={(e) => setEnableV2(e.target.checked)}
              />

              <h5>Type Mix</h5>
              <Table bordered size="sm">
                <thead>
                  <tr>
                    <th>Stacker Type</th>
                    <th>Units</th>
                    <th>Posts Override</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {typeMix.map((row, index) => (
                    <tr key={`mix-${index}`}>
                      <td>
                        <Form.Select
                          value={row.typeId}
                          onChange={(e) => updateTypeMixRow(index, "typeId", e.target.value)}
                        >
                          <option value="">Select type...</option>
                          {typeOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </Form.Select>
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          min="0"
                          value={row.totalUnits}
                          onChange={(e) => updateTypeMixRow(index, "totalUnits", e.target.value)}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          min="0"
                          value={row.posts}
                          onChange={(e) => updateTypeMixRow(index, "posts", e.target.value)}
                        />
                      </td>
                      <td>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() =>
                            setTypeMix((prev) => prev.filter((_, idx) => idx !== index))
                          }
                          disabled={typeMix.length === 1}
                        >
                          Remove
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <Button variant="outline-secondary" onClick={() => setTypeMix((prev) => [...prev, makeEmptyTypeMix()])}>
                Add Type
              </Button>

              <div className="mt-4">
                <h6>Unit Assignment Preview (auto-generated)</h6>
                <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid #ddd" }}>
                  <Table bordered size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>Unit</th>
                        <th>Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewUnits.length ? (
                        previewUnits.map((unit) => {
                          const selectedType = typeOptions.find(
                            (opt) => Number(opt.value) === Number(unit.typeId)
                          );
                          const isNew = existingUnitsForProject.length > 0 &&
                            !existingUnitsForProject.some((u) => Number(u.unitNumber) === Number(unit.unitNumber));
                          return (
                            <tr key={`unit-preview-${unit.unitNumber}`}>
                              <td>Unit {unit.unitNumber}{isNew ? <span className="text-success ms-2">(new)</span> : null}</td>
                              <td>{selectedType?.label || "Unknown Type"}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={2} className="text-center">
                            No units yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>

              <div className="mt-4">
                <Button onClick={saveProjectSetup} disabled={isSavingProject}>
                  {isSavingProject ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    "Save Project Setup"
                  )}
                </Button>
              </div>
            </Card.Body>
          </Card>
        ) : null}
      </div>
    </>
  );
}
